/* OCR-ALUMNO (PR #347, §C2) — EL PANEL DE FOTOS DEL ALUMNO.

   Vive dentro del recuadro de subida (reemplaza al ícono y a las tres líneas mientras
   haya fotos) y, cuando la cuenta ya tiene documentos, como tarjeta encima de la lista.
   Orden de arriba a abajo (§C2): Nombre · línea 1 (conteo + toque) · línea 2 (orden,
   NOTORIA) · cuadrícula numerada con el mosaico «+» · botón «Subir N fotos» + Cancelar.

   El componente NO sube nada: recibe las fotos, el orden y los callbacks del Panel.
   Tokens: la línea 1 lleva el mismo tono que la nueva línea de formatos del recuadro;
   la línea 2 lleva el tono del título («Suelta aquí tu material de estudio»), peso 500 y
   un paso de tamaño arriba. Sin ícono, sin recuadro, sin ámbar. */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { numeroDe } from '../lib/fotosAlumno';

/* El botón primario naranja de la casa (referencia: `start-study-btn`, Dashboard.js). */
export const CLASES_BOTON_PRIMARIO = 'bg-[#FF6B00] hover:bg-[#E05A00] active:bg-[#E05A00] active:scale-[0.98] active:duration-0 text-white rounded-full';

function Miniatura({ foto, numero, onAlternar, t }) {
  const [rota, setRota] = useState(!foto.url);
  const incluida = numero != null;
  return (
    <button type="button"
      onClick={(e) => { e.stopPropagation(); onAlternar(foto.id); }}
      className={`relative aspect-square min-h-[44px] min-w-[44px] rounded-lg overflow-hidden bg-[#1f1f1f] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/60 ${incluida ? '' : 'opacity-40'}`}
      aria-pressed={incluida}
      aria-label={foto.nombre}
      data-testid={`foto-${foto.id}`}>
      {rota ? (
        <div className="w-full h-full flex flex-col items-center justify-center px-1" data-testid={`foto-gris-${foto.id}`}>
          <span className="text-[12px] text-[#a0a0a0] truncate max-w-full">{foto.nombre}</span>
        </div>
      ) : (
        <img src={foto.url} alt="" className="w-full h-full object-cover" onError={() => setRota(true)} draggable={false} />
      )}
      {incluida && (
        <span className="absolute top-1 left-1 min-w-[20px] h-5 px-1 rounded-full bg-[#FF6B00] text-white text-[12px] font-semibold flex items-center justify-center"
          data-testid={`foto-numero-${foto.id}`}>
          {numero}
        </span>
      )}
    </button>
  );
}

/* dashboard-rediseno, segunda vuelta (D12 y D18): el numero de orden y el nombre de la foto rota
   pasan a 12 px (la letra minima). `enVentana` (la ventana de F5, `panel/VentanaDeFotos.js`) parte
   el panel en un cuerpo que se desplaza y un pie fijo con los botones; sin ella, el panel de
   siempre, nodo por nodo. */
export default function PanelFotos({
  fotos, orden, nombreInicial, subiendo, error,
  onAlternar, onAgregarMas, onCancelar, onSubir, onOrden, t, enVentana = false,
}) {
  const [nombre, setNombre] = useState(nombreInicial || '');
  useEffect(() => { setNombre(nombreInicial || ''); }, [nombreInicial]);
  const n = orden.length;
  const nombreLimpio = nombre.trim();
  const deshabilitado = subiendo || n === 0 || nombreLimpio.length === 0;

  const cuerpo = (
    <>
      <label className="block text-xs text-[#8a8a8a] mb-1" htmlFor="panel-fotos-nombre">
        {t('dashboard:fotos.nombre_label')}
      </label>
      <Input id="panel-fotos-nombre" value={nombre} maxLength={120}
        onChange={(e) => setNombre(e.target.value)}
        className="w-full bg-[#141414] border-[#333] text-white text-sm min-h-[44px] mb-3"
        data-testid="panel-fotos-nombre" />

      <p className="text-sm text-[#8a8a8a] break-words" data-testid="panel-fotos-linea1">
        {t('dashboard:fotos.linea1', { count: n })}
      </p>
      <p className="text-base font-medium text-white break-words mt-1 mb-3" data-testid="panel-fotos-linea2">
        {t('dashboard:fotos.linea2')}
      </p>

      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="panel-fotos-grid">
        {fotos.map((foto) => (
          <Miniatura key={foto.id} foto={foto} numero={numeroDe(orden, foto.id)} onAlternar={onAlternar} t={t} />
        ))}
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onAgregarMas(); }}
          className="aspect-square min-h-[44px] min-w-[44px] rounded-lg border-2 border-dashed border-[#333] text-[#8a8a8a] text-2xl flex items-center justify-center hoverable:border-[#FF6B00]/60 hoverable:text-[#FF6B00] active:border-[#FF6B00]/60 active:duration-0"
          aria-label={t('dashboard:fotos.agregar_mas')}
          data-testid="panel-fotos-mas">
          +
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-400 mt-3 break-words" data-testid="panel-fotos-error">{error}</p>
      )}

    </>
  );
  const acciones = (conAire) => (
      <div className={`flex flex-col sm:flex-row sm:items-center gap-2${conAire ? ' mt-4' : ''}`}>
        <Button onClick={() => onSubir(nombreLimpio)} disabled={deshabilitado}
          className={`${CLASES_BOTON_PRIMARIO} w-full sm:w-auto min-h-[44px] px-6 text-sm font-medium disabled:opacity-50`}
          data-testid="panel-fotos-subir">
          {subiendo && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {t('dashboard:fotos.subir', { count: n })}
        </Button>
        <button type="button" onClick={onCancelar} disabled={subiendo}
          className="text-sm text-[#a0a0a0] hover:text-white transition-colors min-h-[44px] px-3"
          data-testid="panel-fotos-cancelar">
          {t('common:cancel')}
        </button>
        {/* REMATES-OCR-ALUMNO (PR #348, #332): a la derecha de Cancelar, con su mismo
            estilo. Con >= 1 marcada quita todos los números (las fotos se quedan en el
            panel, apagadas); con 0 numera todas en el orden de la cuadrícula. */}
        <button type="button" disabled={subiendo}
          onClick={() => onOrden && onOrden(n > 0 ? [] : fotos.map((f) => f.id))}
          className="text-sm text-[#a0a0a0] hover:text-white transition-colors min-h-[44px] px-3"
          data-testid="panel-fotos-todas">
          {t(n > 0 ? 'dashboard:fotos.desmarcar_todas' : 'dashboard:fotos.marcar_todas')}
        </button>
      </div>
  );

  if (enVentana) {
    return (
      <div className="flex-1 min-h-0 flex flex-col text-left" onClick={(e) => e.stopPropagation()} data-testid="panel-fotos">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 md:pt-6 pb-2">{cuerpo}</div>
        <div className="shrink-0 px-6 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] md:pb-6 border-t border-[#2A2A2A]"
          data-testid="panel-fotos-pie">
          {acciones(false)}
        </div>
      </div>
    );
  }
  return (
    <div className="text-left" onClick={(e) => e.stopPropagation()} data-testid="panel-fotos">
      {cuerpo}
      {acciones(true)}
    </div>
  );
}
